import { ApiError } from "../../api/apiError";
import { requestErrorText, type TFunction } from "../queue/requestErrorText";
import {
  HTTP_BAD_GATEWAY,
  HTTP_CONFLICT,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
  HTTP_UNPROCESSABLE_ENTITY,
} from "./api";
import type { CredentialHolder } from "./decommissionApi";

const CODE_CREDENTIAL_HOLDERS = "credential_holders_present";

const KEYS = "connections.tokens.botAccounts";

// Тексты отказов забывания по паре статус + код (ADR-0091 § 7); коды —
// docs/spec/modules/repo_host/bot_accounts.md и ядро (`permission_denied`).
const REFUSAL_MESSAGE_KEYS: readonly (readonly [number, string, string])[] = [
  [
    HTTP_CONFLICT,
    "bot_is_connection_principal",
    `${KEYS}.errorConnectionPrincipal`,
  ],
  [HTTP_CONFLICT, CODE_CREDENTIAL_HOLDERS, `${KEYS}.holdersTitle`],
  [
    HTTP_CONFLICT,
    "provider_action_unsupported",
    `${KEYS}.errorProviderUnsupported`,
  ],
  [
    HTTP_CONFLICT,
    "connection_principal_unresolved",
    `${KEYS}.errorPrincipalUnresolved`,
  ],
  [HTTP_NOT_FOUND, "bot_account_not_found", `${KEYS}.errorNotFound`],
  [HTTP_BAD_GATEWAY, "host_unreachable", `${KEYS}.errorUnreachable`],
  [
    HTTP_UNPROCESSABLE_ENTITY,
    "invalid_name_form",
    `${KEYS}.errorInvalidUsername`,
  ],
  [HTTP_FORBIDDEN, "permission_denied", `${KEYS}.errorForbidden`],
];

const HOLDER_MATCHES: ReadonlySet<string> = new Set([
  "source",
  "owner",
  "owner_unknown",
  "source_unknown",
]);

// Элемент перечня держателей той формы, которую умеет показать модалка.
function isCredentialHolder(item: unknown): item is CredentialHolder {
  if (typeof item !== "object" || item === null) {
    return false;
  }
  const { holder_kind, match, role, account, count } = item as Record<
    string,
    unknown
  >;
  return (
    typeof holder_kind === "string" &&
    typeof match === "string" &&
    HOLDER_MATCHES.has(match) &&
    (role === null || typeof role === "string") &&
    (account === null || typeof account === "string") &&
    typeof count === "number"
  );
}

// Держатели выданных значений из отказа 409, иначе `null`. Перечень с
// элементом чужой формы — тоже `null`: прерывать держателей вслепую нельзя,
// модалка покажет текст отказа без шага выбора.
export function credentialHoldersOf(
  err: unknown,
): readonly CredentialHolder[] | null {
  if (
    err instanceof ApiError &&
    err.status === HTTP_CONFLICT &&
    err.code === CODE_CREDENTIAL_HOLDERS &&
    Array.isArray(err.data.holders) &&
    err.data.holders.every(isCredentialHolder)
  ) {
    return err.data.holders;
  }
  return null;
}

// Человекочитаемый текст отказа забывания: по паре статус + код, прочее —
// по статусу ответа либо «нет связи». Технический `message` не выводится.
export function describeDecommissionError(err: unknown, t: TFunction): string {
  if (err instanceof ApiError) {
    const entry = REFUSAL_MESSAGE_KEYS.find(
      ([status, code]) => status === err.status && code === err.code,
    );
    if (entry !== undefined) return t(entry[2]);
  }
  return requestErrorText(err, t);
}
